import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, PenLine, ChevronDown, Heart, Check, Repeat } from "lucide-react";
import { SECTIONS, SECTION_MAP } from "@/lib/sections";

const RECENT_ROOMS_KEY = "salon.compose.recent_rooms";
const readRecentRooms = () => {
  try {
    const raw = localStorage.getItem(RECENT_ROOMS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((s) => SECTION_MAP[s]) : [];
  } catch { return []; }
};
const pushRecentRoom = (slug) => {
  try {
    const prev = readRecentRooms().filter((s) => s !== slug);
    const next = [slug, ...prev].slice(0, 3);
    localStorage.setItem(RECENT_ROOMS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
};

const Compose = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Respect ?type=blog|photo|video|media from a linked CTA and ?room=<slug> from the "+ Post here" flow
  const _sp = new URLSearchParams(location.search);
  const _paramType = _sp.get("type");
  const _paramRoom = _sp.get("room");
  const initialType = ["photo", "video", "blog", "media"].includes(_paramType) ? _paramType : "media";
  const [type, setType] = useState(initialType === "photo" || initialType === "video" ? "media" : initialType);
  const [section, setSection] = useState(_paramRoom || null);
  const [caption, setCaption] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploadedMediaType, setUploadedMediaType] = useState(""); // 'image' | 'video' — decided by file mime
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cloudinaryReady, setCloudinaryReady] = useState(false);
  const [favoriteRooms, setFavoriteRooms] = useState([]);
  const [recentRooms, setRecentRooms] = useState(readRecentRooms());
  const [favBusy, setFavBusy] = useState(false);
  // When a room is set via ?room= or via a room-page CTA, the composer becomes
  // a clean 2-step flow (media → text). The picker is hidden. Tapping "change"
  // toggles this true to reveal the picker inline.
  const [showPickerOverride, setShowPickerOverride] = useState(false);

  useEffect(() => {
    api.get("/cloudinary/status").then(({ data }) => setCloudinaryReady(data.configured));
    api.get("/auth/me").then(({ data }) => {
      setFavoriteRooms(data?.favorite_rooms || []);
      // Pre-select a sensible default room when none provided via ?room=
      if (!_paramRoom) {
        const recents = readRecentRooms();
        const preferred = recents[0] || (data?.favorite_rooms || [])[0] || null;
        if (preferred) setSection(preferred);
      }
    }).catch(() => {});
  }, []);

  const toggleFavoriteRoom = async (slug) => {
    const next = favoriteRooms.includes(slug)
      ? favoriteRooms.filter((s) => s !== slug)
      : [...favoriteRooms, slug];
    setFavoriteRooms(next);
    setFavBusy(true);
    try {
      const meResp = await api.get("/auth/me");
      await api.put("/users/me/favorite-rooms", {
        favorite_rooms: next,
        rooms_digest_email: meResp.data?.rooms_digest_email !== false,
      });
    } catch {
      toast.error("Couldn't save favorite.");
      setFavoriteRooms(favoriteRooms); // rollback
    } finally {
      setFavBusy(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    if (!cloudinaryReady) {
      toast.error("Direct uploads require Cloudinary credentials. For now, paste a URL below.");
      return;
    }
    setUploading(true);
    try {
      const resourceType = isVideo ? "video" : "image";
      const sig = await api.get("/cloudinary/signature", { params: { resource_type: resourceType, folder: "posts/" } });
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.data.api_key);
      form.append("timestamp", sig.data.timestamp);
      form.append("signature", sig.data.signature);
      form.append("folder", sig.data.folder);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/${resourceType}/upload`,
        { method: "POST", body: form }
      );
      const data = await res.json();
      if (data.secure_url) {
        setMediaUrl(data.secure_url);
        setUploadedMediaType(isVideo ? "video" : "image");
        toast.success("Uploaded.");
      } else {
        toast.error("Upload failed.");
      }
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!section) return toast.error("Please choose a room.");
    if (type === "blog" && (!title.trim() || !body.trim())) return toast.error("A letter needs a title and a body.");
    if (type !== "blog" && !caption.trim() && !mediaUrl) return toast.error("A caption or a photo/video, please.");
    setBusy(true);
    try {
      // For "media" (Photo/Video), infer the actual post_type from the file that was uploaded.
      const resolvedType =
        type === "blog"
          ? "blog"
          : (uploadedMediaType === "video" ? "video" : "photo");
      const payload = {
        post_type: resolvedType,
        section,
        caption: resolvedType === "blog" ? "" : caption,
        title: resolvedType === "blog" ? title : "",
        body: resolvedType === "blog" ? body : "",
        media_url: mediaUrl,
        media_type: mediaUrl ? uploadedMediaType || "image" : "",
        tags: tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      };
      const { data } = await api.post("/posts", payload);
      // Remember this room as recent for future compose sessions
      pushRecentRoom(section);
      setRecentRooms(readRecentRooms());
      toast.success("Published.");
      navigate(`/post/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish");
    } finally {
      setBusy(false);
    }
  };

  // Room is "locked" only when the user arrived via ?room= (from a room-page CTA
  // or the `+ Post here` room card). Auto-preselecting a favorite/recent as a
  // convenience must NOT trigger the lock — otherwise a user visiting /compose
  // directly would see a room-specific header they never opted into.
  const roomLocked = !!_paramRoom && !!section && !showPickerOverride;

  const isLetter = type === "blog";
  const roomPicker = (
    <div>
      <label className="overline" style={{ color: "var(--brand-accent)" }}>
        {type === "blog" ? "In which room does this letter belong? *" : "Post to *"}
      </label>
      {type === "blog" ? (
        // Compact chip picker for letters — takes less visual space
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          {SECTIONS.map((s) => {
            const active = section === s.slug;
            return (
              <button
                key={s.slug}
                type="button"
                data-testid={`section-pick-${s.slug}`}
                onClick={() => setSection(s.slug)}
                style={{
                  background: active ? s.accent : "var(--surface)",
                  color: active ? "var(--text-inverse)" : "var(--text-primary)",
                  border: active ? `1px solid ${s.accent}` : "1px solid var(--border-default)",
                  padding: "8px 14px",
                  fontSize: 13,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      ) : (
        <SmartRoomDropdown
          section={section}
          setSection={setSection}
          favoriteRooms={favoriteRooms}
          recentRooms={recentRooms}
          onToggleFavorite={toggleFavoriteRoom}
          favBusy={favBusy}
        />
      )}
    </div>
  );

  return (
    <div data-testid="compose-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />
      <div className="container-belle" style={{ paddingTop: 110, paddingBottom: 80, maxWidth: 800 }}>
        <div className="overline" style={{ color: "var(--brand-accent)" }}>{isLetter ? "Write a letter" : "Publier"}</div>
        <h1 className="editorial-h1" style={{ fontSize: "clamp(36px, 5vw, 56px)", marginTop: 12, marginBottom: 12 }}>
          {isLetter ? "Begin your letter." : (roomLocked && SECTION_MAP[section] ? `Share a moment in ${SECTION_MAP[section].label}.` : "Share something beautiful.")}
        </h1>
        {isLetter && (
          <p className="editorial-body" style={{ fontSize: 16, marginBottom: 32, color: "var(--text-secondary)", fontStyle: "italic", maxWidth: 560 }}>
            A long-form note. Give it a title, write freely, then choose which room it belongs in.
          </p>
        )}

        {/* Room-locked header — shown only when the user arrived via ?room=
            (i.e. a room-page "Post here" CTA). Auto-preselection from favorites
            or recents helps the dropdown default nicely but does NOT trigger
            this lock — that would surprise users landing on /compose directly. */}
        {!isLetter && roomLocked && SECTION_MAP[section] && (
          <div
            data-testid="compose-room-lock-header"
            style={{
              display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
              padding: "12px 18px", marginTop: 4, marginBottom: 28,
              background: "var(--bg-alt)",
              borderLeft: `3px solid ${SECTION_MAP[section].accent}`,
            }}
          >
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: SECTION_MAP[section].accent, flex: "0 0 auto" }} />
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "var(--text-primary)" }}>
                Posting in <strong style={{ color: SECTION_MAP[section].accent, fontWeight: 500 }}>{SECTION_MAP[section].label}</strong>
              </span>
            </div>
            <button
              type="button"
              data-testid="compose-change-room"
              onClick={() => setShowPickerOverride(true)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "transparent",
                color: "var(--brand-accent)",
                border: "1.5px solid var(--brand-accent)",
                padding: "8px 16px",
                fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                fontFamily: "'Outfit', sans-serif", fontWeight: 600,
                cursor: "pointer",
                transition: "background 0.18s ease, color 0.18s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--brand-accent)"; e.currentTarget.style.color = "#FAF9F5"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--brand-accent)"; }}
            >
              <Repeat size={13} strokeWidth={2} /> Change room
            </button>
          </div>
        )}

        {/* Type-mode tabs removed from the visible UI (per Mimi, July 14 2026).
            The composer now shows Photo/Video by default; Letter mode is still
            reachable via ?type=blog (used by the "Write a letter" CTA on
            /journal). Keeping type=blog URL support means no broken links. */}

        {/* For photo/video: only show the room picker if the room is NOT locked
            (i.e. no ?room= param or user tapped "change room"). Letters always
            show their compact chip picker at the end of the form. */}
        {!isLetter && !roomLocked && <div style={{ marginBottom: 36 }}>{roomPicker}</div>}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {isLetter && (
            <>
              <div>
                <label className="overline">Title of the letter</label>
                <input data-testid="compose-title-input" required={isLetter} value={title} onChange={(e) => setTitle(e.target.value)} className="input-belle" placeholder="A short letter on butter" />
              </div>
              <div>
                <label className="overline">The letter</label>
                <textarea
                  data-testid="compose-body-input"
                  required={isLetter}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="input-belle"
                  rows={12}
                  style={{ minHeight: 280, resize: "vertical", borderBottom: "1px solid var(--border-dark)", fontFamily: "'Cormorant Garamond', serif", fontSize: 19, lineHeight: 1.7 }}
                  placeholder="Begin here…"
                />
              </div>
            </>
          )}

          {type !== "blog" && (
            <div>
              <label className="overline">Caption</label>
              <textarea
                data-testid="compose-caption-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="input-belle"
                rows={3}
                style={{ minHeight: 80, resize: "vertical", borderBottom: "1px solid var(--border-dark)", fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontStyle: "italic" }}
                placeholder="A quiet thought to accompany the image…"
              />
            </div>
          )}

          {/* Media area */}
          <div>
            <label className="overline">{type === "blog" ? "Cover image (optional)" : "Photo or video"}</label>
            <div style={{ marginTop: 8, padding: 24, border: "1px dashed var(--border-dark)", background: "var(--bg-alt)" }}>
              {mediaUrl ? (
                <div>
                  {uploadedMediaType === "video" ? (
                    <video src={mediaUrl} controls style={{ width: "100%", maxHeight: 400 }} />
                  ) : (
                    <img src={mediaUrl} alt="" style={{ width: "100%", maxHeight: 400, objectFit: "contain" }} />
                  )}
                  <button type="button" onClick={() => { setMediaUrl(""); setUploadedMediaType(""); }} style={{ marginTop: 12, background: "transparent", border: "none", color: "var(--brand-accent)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", cursor: "pointer" }}>
                    Remove media
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", textAlign: "center", padding: "16px 0" }}>
                  <label className="btn-secondary" style={{ cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.5 : 1 }}>
                    {uploading ? "Uploading…" : (cloudinaryReady ? "Upload from device" : "Direct upload — needs Cloudinary keys")}
                    <input
                      type="file"
                      data-testid="compose-file-input"
                      accept={type === "blog" ? "image/*" : "image/*,video/*"}
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      disabled={!cloudinaryReady || uploading}
                      style={{ display: "none" }}
                    />
                  </label>
                  <span className="overline" style={{ opacity: 0.6 }}>or</span>
                  <input
                    data-testid="compose-media-url-input"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder="Paste an image or video URL"
                    className="input-belle"
                    style={{ maxWidth: 520, textAlign: "center" }}
                  />
                  {!cloudinaryReady && (
                    <p style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 420, lineHeight: 1.6, marginTop: 4 }}>
                      <em>Demo mode:</em> paste any image URL (e.g. from Unsplash) to preview. Real uploads activate once Cloudinary keys are added.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="overline">Tags (comma-separated)</label>
            <input data-testid="compose-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} className="input-belle" placeholder="paris, café, morning" />
          </div>

          {/* For letters: the room picker appears at the END so the writer can focus on writing first. */}
          {isLetter && roomPicker}

          <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
            <button type="submit" disabled={busy} data-testid="compose-submit-button" className="btn-primary">
              {busy ? "Publishing…" : "Publish"}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SmartRoomDropdown = ({ section, setSection, favoriteRooms, recentRooms, onToggleFavorite, favBusy }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = section ? SECTION_MAP[section] : null;

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Build grouped list, de-duplicating between groups
  const recents = (recentRooms || []).filter((s) => SECTION_MAP[s]).slice(0, 3);
  const favs = (favoriteRooms || []).filter((s) => SECTION_MAP[s] && !recents.includes(s));
  const others = SECTIONS.map((s) => s.slug).filter((slug) => !recents.includes(slug) && !favs.includes(slug));

  const groups = [
    { key: "recent", label: "Recently posted in", slugs: recents },
    { key: "favs", label: "Your favorites", slugs: favs },
    { key: "all", label: recents.length || favs.length ? "All rooms" : "Rooms", slugs: others },
  ].filter((g) => g.slugs.length > 0);

  const rowFor = (slug) => {
    const s = SECTION_MAP[slug];
    const active = section === slug;
    const isFav = favoriteRooms.includes(slug);
    return (
      <div
        key={slug}
        data-testid={`room-option-${slug}`}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px",
          background: active ? "var(--bg-alt)" : "transparent",
          borderLeft: active ? `2px solid ${s.accent}` : "2px solid transparent",
          transition: "background 0.15s ease",
        }}
      >
        <button
          type="button"
          data-testid={`section-pick-${slug}`}
          onClick={() => { setSection(slug); setOpen(false); }}
          style={{
            all: "unset", cursor: "pointer", flex: 1,
            display: "flex", alignItems: "center", gap: 12,
            fontFamily: "'Cormorant Garamond', serif", fontSize: 18,
            color: "var(--text-primary)",
          }}
          onMouseEnter={(e) => { if (!active) e.currentTarget.parentElement.style.background = "var(--bg-alt)"; }}
          onMouseLeave={(e) => { if (!active) e.currentTarget.parentElement.style.background = "transparent"; }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.accent, flex: "0 0 auto" }} />
          <span>{s.label}</span>
          {active && <Check size={14} strokeWidth={2} color={s.accent} style={{ marginLeft: 4 }} />}
        </button>
        <button
          type="button"
          data-testid={`room-option-heart-${slug}`}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(slug); }}
          disabled={favBusy}
          aria-label={isFav ? `Remove ${s.label} from favorites` : `Add ${s.label} to favorites`}
          title={isFav ? "Favorite — click to remove" : "Add to favorites"}
          style={{
            background: "transparent", border: "none", cursor: favBusy ? "wait" : "pointer",
            padding: 4, display: "inline-grid", placeItems: "center",
          }}
        >
          <Heart size={16} strokeWidth={1.6} fill={isFav ? s.accent : "none"} color={isFav ? s.accent : "var(--text-muted)"} />
        </button>
      </div>
    );
  };

  return (
    <div ref={rootRef} style={{ position: "relative", marginTop: 8, maxWidth: 480 }}>
      <button
        type="button"
        data-testid="room-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px",
          background: "var(--surface)",
          border: "1px solid var(--border-dark)",
          cursor: "pointer",
          fontFamily: "'Cormorant Garamond', serif",
          textAlign: "left",
          transition: "border-color 0.15s ease",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {selected ? (
            <>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: selected.accent }} />
              <span style={{ fontSize: 19, color: "var(--text-primary)" }}>{selected.label}</span>
            </>
          ) : (
            <span style={{ fontSize: 17, color: "var(--text-muted)", fontStyle: "italic" }}>Choose a room…</span>
          )}
        </span>
        <ChevronDown size={16} strokeWidth={1.6} color="var(--text-secondary)"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
      </button>

      {open && (
        <div
          data-testid="room-dropdown-menu"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
            boxShadow: "0 12px 40px rgba(51,51,51,0.10)",
            zIndex: 60,
            maxHeight: 420,
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          {groups.map((g, i) => (
            <div key={g.key}>
              {i > 0 && <div style={{ borderTop: "1px solid var(--border-default)", margin: "6px 0" }} />}
              <div
                className="overline"
                style={{ padding: "8px 14px 4px", color: "var(--text-muted)", fontSize: 10, letterSpacing: "0.2em" }}
              >
                {g.label}
              </div>
              {g.slugs.map(rowFor)}
            </div>
          ))}
          <div style={{ borderTop: "1px solid var(--border-default)", padding: "8px 14px", marginTop: 4 }}>
            <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0, fontStyle: "italic", lineHeight: 1.4 }}>
              Tap the heart to save a room. Favorites appear at the top next time.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const TypeTab = ({ active, onClick, icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{
      background: "transparent", border: "none", cursor: "pointer",
      padding: "14px 4px", marginRight: 28, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase",
      color: active ? "var(--brand-accent)" : "var(--text-secondary)",
      borderBottom: active ? "1px solid var(--brand-accent)" : "1px solid transparent",
      marginBottom: -1, display: "flex", alignItems: "center", gap: 8, fontWeight: 500,
    }}
  >
    {icon} {label}
  </button>
);

export default Compose;
